/**
 * User 有关信息
 */
import { accoutType } from 'src/const/account';
import apis from 'src/const/apis';
import {
  accountRole, advertiserStatus, qualificationImgsType, QualificationStatus, QualificationType, QualificationSource, STATE_GEOID,
} from 'src/const/state/account';
import * as types from 'src/store/types';
import { getUpdateAccountInfoData } from 'src/utils/selfAccountUtil';
import request from 'src/utils/request';
import moment from 'moment-timezone';
import { currencyLocale } from 'src/const/currencyLocale';
import { getAbVersion } from 'src/service/index';
import { dashboardAccountStatus } from 'src/service/dashboard';

const user = {
  namespaced: true, // 带 account 命名空间
  state: {
    ttcmAccountId: '',
    accountStatus: '',
    accountInfo: {}, // advertiser 账户详情info
    contactInfo: {
      sensitive: {},
      not_sensitive: {},
    }, // 联系人信息
    permissionData: {}, // permission 相关信息
    hasFetchedAccountInfo: false, // 是否已经使用 advertiser/info 接口拉取过完整账户信息
    roles: [],
    showNavbar: false,
    aadvid: '',
    advertiserName: '',
    allAccountList: [],
    authStatus: false,
    coreUserInfo: {}, // 登录状态检测的 coreUserInfo
    tzList: [], // { timezone: string, gmt_offset: string, i18n_key: string }[] 所有可选地点格式时区
    shopifyDetail: {}, // shopify 预填信息
    whiteListObj: {}, // 白名单
    couponTotalBalance: 0,
    qualificationList: [],
    taxValue: {},
    utcOffset: null,
    accountOwnBC: {}, // 通过adv账号去查当前账号对应的bc账号的own bc相关的数据
    isTTP: false, // 是否 ttp 打标
    fixedOffsetTimezone: null,
    hasNeedPostInvoiceInfo: false,
    currencyLocale: null, // 货币本地化配置信息,
    enableHideComment: false, // 是否命中comment ab实验并且隐藏评论
    creationStatistics: {}, // adv 下的 campaign、adgroup、ad 数量
  },
  getters: {
    isStaffIdExist: state => !!(state.permissionData?.staff?.staff_id),
    adverId: state => state.permissionData?.account?.id || '',
    adverName: state => state.permissionData?.account.name || '',
    currency: state => state.permissionData?.account?.currency || '',
    accountType: state => state.permissionData?.account?.customer_type_value,
    // 切记 ！！！accountInfo 接口只在一些页面（资质页面等）才有，不能用 accountInfo 生成可能在全局使用的 hook
    // 只能在有拉取 advertiser/info 接口的地方使用
    accountInfo: state => state.accountInfo || {},
    loginId: state => state.permissionData?.account?.id || '0',
    visitorId: state => state.permissionData?.staff?.staff_id || '0',
    accountCreateTime: state => state.permissionData?.account?.create_time,
    accountNum: (state) => {
      if (state.allAccountList.length === 0) {
        return 0;
      }

      let num = 0;
      state.allAccountList.forEach((item) => {
        if (+item.role !== accoutType.STATUS_BUSINESS_MANAGER && +item.role !== accoutType.STATUS_PENDING_CONFIRM) {
          num += 1;
        } else {
          num += item.child ? item.child.length : 0;
        }
      });

      return num;
    },
    orgNum: (state) => {
      const list = state.allAccountList.filter(item => (
        +item.role === accoutType.STATUS_BUSINESS_MANAGER ||
        +item.role === accoutType.STATUS_PENDING_CONFIRM
      ));
      return list.length;
    },
    firstAccount: (state) => {
      const role = (state.allAccountList[0] && +state.allAccountList[0].role);

      if (role === undefined) {
        return { id: '', name: '' };
      }

      if (role !== accoutType.STATUS_BUSINESS_MANAGER && role !== accoutType.STATUS_PENDING_CONFIRM) {
        return state.allAccountList[0];
      }
      const listParents = state.allAccountList.filter(item => item.child.length > 0);
      return listParents[0] || { id: '', name: '' };
    },
    accountRole: state => state?.permissionData?.account?.role,
    qualificationSource: state => +state.permissionData?.account?.qualification_source,
    isBeCompleted: state => +state.accountStatus === advertiserStatus.PROMOTION_SELF_SERVICE, // 是否自助待补全资质
    isSelfAccount: state => +state.permissionData?.account?.customer_type_value === accountRole.ROLE_SLEF_ACCOUNT, // 是否自助客户
    isKaAccount: state => +state.permissionData?.account?.customer_type_value === accountRole.ROLE_COMMON_ACCOUNT, // 是否 ka 客户
    isVirtualAccount: state => +state.permissionData?.account?.customer_type_value === accountRole.ROLE_VIRTAUL_ACCOUNT, // 是否虚客
    // https://bytedance.feishu.cn/docs/doccn1V1DQYMRaZCKTFgRqaPhEf 判断规则
    isCommonSelfAccount: (_, getters) => getters.isSelfAccount
      && getters.accountRole === 0
      && getters.qualificationSource === QualificationSource.BC, // 是否直客自助客户
    isVirtaulSelfAccount: (_, getters) => getters.isSelfAccount
      && getters.accountRole === 2
      && getters.qualificationSource === QualificationSource.NOT_FROM_BC, // 是否虚客自助客户
    isSelfBcAccount: (_, getters) => getters.isCommonSelfAccount || getters.isVirtaulSelfAccount, // 是否自助 BC ｜ 自助bc 子账户，bc自助直客 + bc自助虚客
    isNotBcChildSelfAccount: (_, getters) => getters.isSelfAccount
      && getters.accountRole === 0
      && getters.qualificationSource === QualificationSource.NOT_FROM_BC, // 自助但非自助bc
    isRoleVirtualAccount: (_, getters) => getters.accountRole === 2, // 这里可能判断有问题，但是是历史逻辑，先不动了
    localeTzList: state => state.tzList || [],
    shopifyDetail: state => state.shopifyDetail || {},
    permissionData: state => state.permissionData || {},
    qualificationListKey(state, getters, rootState, rootGetter) {
      // 动态计算
      return `${rootGetter['campaign/modelKey']}:qulification`;
    },
    country: state => state.permissionData?.account?.country || '',
    taxVat: state => state.taxValue?.vat,
    hasOwnBc: state => state.accountOwnBC?.is_own_by_bc && state.accountOwnBC?.is_bc_admin && !!state.accountOwnBC?.org_id,
    orgId: state => state.accountOwnBC?.org_id,
    hasAd: (state) => {
      const { active = 0, spending = 0, not_delivery: notDelivery = 0 } = state.creationStatistics?.campaigns || {};
      // 其中有一个 > 0 则 has_ad = true
      return Number(active + spending + notDelivery) > 0;
    },
    enableSelfQualDialog(state, getters) {
      // 自助非 bc + 为2022 年后创建的 adv，因为2020年可能存在一些未有 business name 或者联系人信息的adv
      // 资质弹窗目前不支持这类信息补充
      return getters.isNotBcChildSelfAccount &&
          new Date(getters.accountCreateTime).valueOf() > new Date('2022-06-24').valueOf();
    },
    // 自助客户是否需要确认补充发票信息
    selfAccountNeedConfirmInvoice(state, getters, rootState, rootGetters) {
      const { hasNeedPostInvoiceInfo } = state;
      const { enableSelfQualDialog } = getters;
      const hasPostInvoiceConfirm = rootGetters['popupStatus/hasPostInvoiceConfirm'];
      return enableSelfQualDialog && hasNeedPostInvoiceInfo && !hasPostInvoiceConfirm;
    },
  },
  mutations: {
    [types.SET_TAX_VALUE](state, taxValue) {
      state.taxValue = taxValue;
    },
    [types.SET_ACCOUNT_INFO](state, accountInfo) {
      state.accountInfo = accountInfo || {};
      state.accountStatus = accountInfo?.status;
    },
    [types.SET_HAS_FETCHED_ACCOUNT_INFO](state, data) {
      state.hasFetchedAccountInfo = data;
    },
    [types.SET_CONTACT_INFO](state, contactInfo) {
      state.contactInfo = {
        ...state.contactInfo,
        ...contactInfo,
      };
    },
    [types.SET_PERMISSION_DATA](state, permissionData) {
      state.permissionData = permissionData;
      state.accountStatus = permissionData?.account?.status;
    },
    [types.SET_CORE_USER_INFO](state, info) {
      state.coreUserInfo = info || {};
    },
    [types.SET_AADVID](state, aadvid) {
      state.aadvid = aadvid;
    },
    [types.SET_ADVERTISERNAME](state, data) {
      state.advertiserName = data;
    },
    [types.SET_ACCOUNT_BALANCE](state, accountBalance) {
      state.accountBalance = accountBalance;
    },
    [types.SET_SHOW_NAVBAR](state, flag) {
      state.showNavbar = !!flag;
    },
    [types.SET_AUTH_STATUS](state, status) {
      state.authStatus = status;
    },

    [types.SET_ACCOUNT_LIST](state, list) {
      state.allAccountList = list;
    },
    [types.SET_TIMEZONELIST](state, localeTzList = []) {
      state.tzList = localeTzList;
    },

    [types.SET_SHOPIFY_DETAIL](state, data = {}) {
      state.shopifyDetail = data;
    },
    [types.SET_WHIHT_LIST](state, data = {}) {
      state.whiteListObj = data;
    },
    [types.SET_COUPON_TOTAL_BALANCE](state, data = 0) {
      state.couponTotalBalance = data;
    },
    [types.SET_QUALIFICATION_LIST](state, data = {}) {
      state.qualificationList = data;
    },
    [types.SET_UTC_OFFSET](state, data) {
      state.utcOffset = data;
    },
    [types.SET_ACCOUNT_OWN_BC](state, data) {
      state.accountOwnBC = data;
    },
    [types.SET_TTP_STATUS](state, data) {
      state.isTTP = data;
    },
    [types.SET_FIXED_OFFSET_TIMEZONE](state, data) {
      state.fixedOffsetTimezone = data;
    },
    [types.SET_HAS_NEED_POST_INVOICE_INFO](state, data) {
      state.hasNeedPostInvoiceInfo = data;
    },
    [types.SET_CURRENCY_LOCALE](state, data) {
      state.currencyLocale = data;
      window.CURRENCY_LOCALE = data;
    },
    [types.SET_TTCM_ACCOUNT_ID](state, data) {
      state.ttcmAccountId = data;
    },
    [types.SET_ENABLE_HIDE_COMMENT](state, data) {
      state.enableHideComment = data;
    },
    [types.SET_CREATION_STATISTICS](state, data) {
      state.creationStatistics = data;
    },
  },

  actions: {
    async getContactInfo({ commit }, { sensitive_flag, is_self_account }) {
      const { code, data, msg } = await request.get(apis.account.contact_info, { sensitive_flag });
      let contactInfo = null;
      if (code === 0) {
        const { contact_info } = data;
        // 明文请求需要使用BASE64解码
        if (sensitive_flag) {
          Object.keys(contact_info).forEach((key) => {
            const value = contact_info[key];
            // 国家码不需要转码
            if (['contacter_telephone_country', 'phonenumber_country'].includes(key)) return;
            try {
              contact_info[key] = value ? decodeURIComponent(escape(atob(value))) : '';
            } catch (err) {
              contact_info[key] = value;
            }
          });
        }
        contactInfo = {
          ...contact_info,
          telephone: contact_info.telephone || (is_self_account ? contact_info.phonenumber : ''), // 自助账号如果telephone不存在用phonenumber默认填充
          contacter_telephone_country: contact_info.contacter_telephone_country || '',
          contacter_email: contact_info.contacter_email || (is_self_account ? contact_info.email : ''), // 自助默认用注册邮箱填充
        };
        if (!is_self_account) {
          contactInfo.phonenumber = contact_info.phonenumber || '';
          contactInfo.phonenumberCountry = contact_info.phonenumber_country || '';
        }
        commit(types.SET_CONTACT_INFO, sensitive_flag ? { sensitive: contactInfo } : { not_sensitive: contactInfo });
      }
      return contactInfo || new Error(msg);
    },
    async getCommentToolNewUser({ commit }) {
      const data = await getAbVersion({ ab_feature: 'comment_tool_new_user' });
      const { hit_comment_tool_ab = false } = data;
      commit(types.SET_ENABLE_HIDE_COMMENT, hit_comment_tool_ab?.hide);
    },
    getNotification() {
      return request.get(apis.account.get_notification);
    },
    majordomoDelete() {
      return request.get(apis.majordomo_delete);
    },

    getCampaignStatus() {
      return request.get(apis.account.ad_info);
    },

    getPONumber() {
      return request.get(apis.account.PONumber);
    },
    updatePONumber(data, payload) {
      return request.post(apis.account.PONumber, payload);
    },

    canChangeBusinessName() {
      return request.get(apis.account.canChangeBusinessName);
    },

    getTotalCost() {
      return request.get(apis.dashboard.dashboardCost, {
        source: 3, // 3 代表tiktok竞价平台
      });
    },
    // 横幅提醒
    getNotifyMsg() {
      return request.get(apis.account.notification_msg);
    },
    // 获取账号列表
    async getAccountList({ commit }) {
      try {
        const { data } = await request.get(apis.account.accountList);
        commit(types.SET_ACCOUNT_LIST, data.data || []);
        commit(types.SET_AUTH_STATUS, true);
      } catch (e) {
        commit(types.SET_ACCOUNT_LIST, []);
        commit(types.SET_AUTH_STATUS, false);
      }
    },

    getAdChartStatus({ commit }, data) {
      return request.get(apis.account.advertiser_chart, data);
    },

    // 获取广告主惩罚消息
    getPunishInfo({ commit }, data) {
      return request.get(apis.account.punish_info, data);
    },

    setAccountAadvidAndName({ commit }, data) {
      const { aadvid, advertiserName } = data;
      try {
        commit(types.SET_AADVID, aadvid);
        commit(types.SET_ADVERTISERNAME, advertiserName);
      } catch (e) {
        commit(types.SET_AADVID, '');
        commit(types.SET_ADVERTISERNAME, '');
      }
    },

    /**
     * 获取账户信息
     * advertiser/info 接口
     */
    async loadAccountInfo({ commit }, params) {
      let data = {};
      try {
        const requestArr = [request.get(apis.account.info, params), user.actions.loadAccountReviewInfo()];
        const [accountData, reviewData] = await Promise.all(requestArr);
        data = { ...accountData.data, ...reviewData };
        // window.LOGIN_INFO = data;

        // 处理美国夏威夷 city => county 后端刷库前，使 存量city 展示为 county;
        if (data.state === STATE_GEOID.HAWAII) {
          data.county = data.county && data.county !== -1 ? data.county : data.city;
          data.city = null;
        }
        // end

        commit(types.SET_ACCOUNT_INFO, data);
        commit(types.SET_HAS_FETCHED_ACCOUNT_INFO, true);
      } catch (e) {
        // window.LOGIN_INFO = {};
        commit(types.SET_ACCOUNT_INFO, {});
        data = {};
      }
      return data;
    },

    /**
     * 加载 permission account info
     * 与 loadAccountInfo 区别：
     * 1. account info 主要在账户信息、资质页等使用，
     * 2. permission info 主要用于访问鉴权登录
     * @param {Object} params 入参
     */
    async loadPermissionData({ commit, dispatch, rootState }, params) {
      let [data] = await request
        .get(apis.account.permission, params)
        .then(data => [data, null])
        .catch(err => [null, err]);
      data = data?.data || {};
      window.PERMISSION_DATA = data;
      const { country = '', currency = '' } = data.account || {};
      const localeValue = currencyLocale[country];
      if (localeValue && localeValue.currency === currency
        && localeValue.apply_lang?.includes(rootState.currentLang)) {
        commit(types.SET_CURRENCY_LOCALE, localeValue);
      } else {
        commit(types.SET_CURRENCY_LOCALE, null);
      }
      commit(types.SET_PERMISSION_DATA, data);
      const advInfo = {
        aadvid: data?.account?.id || '',
        advertiserName: data?.account?.name || '',
      };
      dispatch('setAccountAadvidAndName', advInfo);
      return data;
    },

    /** [TTP] 获取 aadvid 纬度的打标信息 */
    async getTtpTagByAadvid({ commit }, aadvid) {
      let isTTP = false;
      try {
        const res = await request.post(apis.account.getTTPFlag, { object_type: 1, object_id: aadvid });
        /** 1 ttp, 2 non ttp */
        isTTP = res?.data?.region_type === 1;
      } catch (e) {
        isTTP = false;
      }
      commit(types.SET_TTP_STATUS, isTTP);
      return isTTP;
    },

    async loadWhiteList({ commit }) {
      let res = {};
      try {
        res = await request.get(apis.account.white_list);
      } catch (e) {
        res = {};
      }
      const data = res?.data || {};
      commit(types.SET_WHIHT_LIST, data);
      return data;
    },

    // 获取账户附加信息，如时区 offset
    async loadExtra({ state, commit }) {
      try {
        const res = await request.get(apis.account.extra);
        const utcOffset = res.data.timezone.value;
        if (typeof utcOffset === 'number') {
          commit(types.SET_UTC_OFFSET, utcOffset);

          const accountTimezone = state.permissionData?.account?.timezone;
          if (accountTimezone) {
            const fixedOffsetAccountTimeZoneName = `${accountTimezone}/${utcOffset}`;
            const fixedOffsetAccountTimeZoneObject = {
              name: fixedOffsetAccountTimeZoneName,
              abbrs: [moment().utcOffset(utcOffset).format('Z')],
              untils: [null],
              offsets: [utcOffset],
            };
            moment.tz.add(moment.tz.pack(fixedOffsetAccountTimeZoneObject));
            commit(types.SET_FIXED_OFFSET_TIMEZONE, fixedOffsetAccountTimeZoneName);
          }
        }
      } catch {
        return {};
      }
    },

    // 新增接口，获取账户拒审信息
    async loadAccountReviewInfo() {
      let businessReviewData;
      try {
        const { data } = await request.get('/api/v3/i18n/account/qualification/detail/');
        const { qualifications = [] } = data;
        businessReviewData = {
          qualification_reject_reasons: {},
          qualification_new_secret: [],
          qualification_url_secret: [],

        };
        for (let index = 0; index < qualifications.length; index++) {
          const item = qualifications[index];
          if (item.qualification_status === QualificationStatus.reject) {
            const key = item.qualification_type !== qualificationImgsType ? QualificationType[item.qualification_type]
              : item.qualification_value.qualification_url_secret;
            businessReviewData.qualification_reject_reasons[key] = item.reason;
          }
          // 资质图片没有唯一ID，所以这里重新获取一次数据重新对应拒审理由
          if (item.qualification_type === qualificationImgsType) {
            businessReviewData.qualification_new_secret.push(item.qualification_value.qualification_new_secret);
            businessReviewData.qualification_url_secret.push(item.qualification_value.qualification_url_secret);
          }
        }
      } catch (e) {
        businessReviewData = {};
      }
      return businessReviewData;
    },

    // 获取账户登录态检测
    async loadLoginInfo({ commit }) {
      let data;
      try {
        ({ data } = await request.get(apis.account.passport));
        commit(types.SET_CORE_USER_INFO, data);
      } catch (e) {
        commit(types.SET_CORE_USER_INFO, {});
        return Promise.reject(e);
      }
      return data;
    },
    // 账户资质 补全提交
    accountUpdate({ commit }, data) {
      return request.post(apis.account.accountUpdate, getUpdateAccountInfoData(data));
    },
    // 自助首次提交资质
    firstPostAccountInfo({ commit }, data) {
      return request.post(apis.account.firstPostAccountInfo, getUpdateAccountInfoData(data)).catch((err) => {
        // 已经提交过资质，以成功作为兜底
        if (err?.code === 1001) {
          return Promise.resolve();
        }
        return Promise.reject();
      });
    },
    // 沙特税务请求
    getVerificationStatus({ commit }, data) {
      return request.get(apis.account.verificationStatus, data);
    },
    async getTaxInfo({ dispatch, commit }) {
      const { data = {} } = await dispatch('getVerificationStatus').catch(() => ({}));
      const { tax_value } = data;
      commit(types.SET_TAX_VALUE, tax_value || {});
    },
    getGeoByCodeNew({ commit }, data) {
      // 根据geoid查询children 地理中台
      return request.get(apis.account.getGeoByCodeNew, data);
    },
    getGeoByCode({ commit }, data) {
      // 根据geoid查询children toolbox
      return request.get(apis.account.getGeoByCode, data);
    },
    getReginNameByCode({ commit }, data) {
      // 根据geoid查询geoName
      return request.get(apis.account.getReginNameByCode, data);
    },
    cancelAccount({ commit }, data) {
      // 销户
      return request.post(apis.account.cancel_account, data);
    },
    async getLocaleTzList({ commit, getters }, params) {
      // 缓存
      if (getters.localeTzList.length) {
        return getters.localeTzList;
      }
      try {
        const { data: resp } = await request.get(apis.account.all_timezone_list, params);
        /**
         * timezone_list format:
         *
         * [
         *    {
                "i18n_key": "tz_key_Africa/Tunis",
                "timezone": "Africa/Tunis",
                "gmt_offset": "GMT-01:00"
              },
              {
                "i18n_key": "tz_key_Europe/Istanbul",
                "timezone": "Europe/Istanbul",
                "gmt_offset": "GMT-02:00"
              },
          ]
         */
        commit(types.SET_TIMEZONELIST, resp.timezone_list);
        return resp.timezone_list || [];
      } catch (e) {
        commit(types.SET_TIMEZONELIST, []);
        return [];
      }
    },

    checkTaxVat({ commit }, data) {
      return request.post(apis.account.check_tax_vat, data);
    },

    // for closing postpay account
    checkAllAdsOff({ commit }, data) {
      return request.post(apis.close.check_all_ads_off, data);
    },
    getBillStatus({ commit }, data) {
      return request.get(apis.close.bill_status, data);
    },
    getQualificationList({ commit }, data) {
      return request.post(apis.account.getQualificationList, data);
    },
    deleteQualification({ commit }, data) {
      return request.post(apis.account.deleteQualification, data);
    },
    checkIptTextLanguage({ commit }, data) {
      return request.post(apis.account.checkIptTextLanguage, data);
    },
    async checkHasNeedPostInvoiceInfo({ commit }, data) {
      const { data: resp } = await request.get(apis.account.checkHasNeedPostInvoiceInfo, data);
      commit(types.SET_HAS_NEED_POST_INVOICE_INFO, resp.is_post_fulfill_country);
    },
    async saveDraft({ getters, dispatch }, data) {
      const list = await dispatch('getDraft');
      if (!data.timestamp) {
        list.push({
          ...data,
          timestamp: `${Date.now()}`,
        });
      } else {
        let found = false;
        list.forEach((item, index) => {
          if (item.timestamp === data.timestamp) {
            list.splice(index, 1, data);
            found = true;
          }
        });
        if (!found) {
          list.push({
            ...data,
            timestamp: Date.now(),
          });
        }
      }
      window.localStorage.setItem(getters.qualificationListKey, JSON.stringify(list));
    },
    getDraft({ getters }) {
      try {
        return JSON.parse(window.localStorage.getItem(getters.qualificationListKey) || '[]');
      } catch (e) {
        return {};
      }
    },
    async deleteDraft({ getters, dispatch }, timestamp) {
      const list = await dispatch('getDraft');
      for (let i = 0; i < list.length; i++) {
        if (list[i].timestamp === timestamp) {
          list.splice(i, 1);
          break;
        }
      }
      window.localStorage.setItem(getters.qualificationListKey, JSON.stringify(list));
    },
    async getAccountOwnBc({ commit }, params) {
      try {
        const { data } = await request.get(apis.account.bcOwnDetails, params);
        commit(types.SET_ACCOUNT_OWN_BC, data || {});
      } catch (e) {
        commit(types.SET_ACCOUNT_OWN_BC, {});
      }
    },

    async getTTCMAccount({ commit, state }) {
      try {
        const { data } = await request.get(apis.account.checkTTCMAccount, {
          ttam_account_id: state.aadvid,
        });
        commit(types.SET_TTCM_ACCOUNT_ID, data.ttcm_account_id);
      } catch (error) {
        commit(types.SET_TTCM_ACCOUNT_ID, '');
      }
    },
    async getCreationStatistics({ commit }, params = {}) {
      const { dims = 'campaign_id' } = params;
      try {
        const { data = {} } = await dashboardAccountStatus({
          dims,
        });
        commit(types.SET_CREATION_STATISTICS, data);
      } catch (e) {
        commit(types.SET_CREATION_STATISTICS, {});
      }
    },
  },
};

export default user;
